'use client';

import { useQuery } from '@tanstack/react-query';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { queryClient } from 'src/utils/queryClient';

interface AlertData {
	heading: string;
	description: string;
	imageSrc?: string;
	linkUrl?: string;
	linkText?: string;
	pathToAlertOn?: string;
	untilMs?: number;
	/**
	 * If `true`, the alert stays up until the user closes it, instead of
	 * auto-dismissing on a timer.
	 */
	disableAutoDismiss?: boolean;
}

interface MarketAlertData extends Pick<
	AlertData,
	'untilMs' | 'disableAutoDismiss'
> {
	baseSymbol: string;
}

export type Alert =
	| {
			alertType: 'feature' | 'warning';
			alertData: AlertData;
	  }
	| {
			alertType: 'market' | 'collateral';
			alertData: MarketAlertData;
	  };

export type AppParamsProps = {
	status: string;
	message: string;
	alert: Alert;
	statusBar: {
		status: 'warn' | 'error';
		message: string;
		showOnlyForInvolvedUsers?: boolean;
		involvedMarketIndexes?: number[];
		involvedMarketTypes?: ('perp' | 'spot')[];
	};
};

interface AppParamsResponse {
	success: boolean;
	params: AppParamsProps;
	updatedAt: number;
}

const fetchAppParams = async (url: string): Promise<AppParamsProps> => {
	const res = await fetch(url, {
		headers: {
			'Cache-Control': 'no-cache',
		},
	});
	if (!res.ok) {
		throw new Error(`/params request failed: ${res.status}`);
	}
	const data: AppParamsResponse = await res.json();
	if (!data.success) {
		throw new Error('/params response indicated failure');
	}
	return data.params;
};

const useAppParams = () => {
	const paramsUrl = `${Env.dataApiUrl}/params`;

	const { data, error } = useQuery(
		{
			queryKey: ['appParams', paramsUrl],
			queryFn: () => fetchAppParams(paramsUrl),
			refetchInterval: 60 * 1000,
		},
		queryClient
	);

	if (error) {
		console.error(error);
	}

	return data;
};

export default useAppParams;
